import { supabase } from '../supabase/client';
import { setProfile, incrementOperation, decrementOperation, setOperationError } from '../Redux/store';

/**
 * Helper function to update a single profile field with Supabase sync
 * Increments global operation counter before Supabase call
 * Decrements after Supabase call completes (success or error)
 * Updates Redux optimistically before Supabase, reverts on error
 *
 * @param {object} profile - Current profile object
 * @param {string} field - Field name to update (e.g., 'name', 'bio')
 * @param {any} value - New value for the field
 * @param {function} dispatch - Redux dispatch function
 * @returns {Promise<boolean>} - True if successful, false if error
 */
export async function updateProfileField(profile, field, value, dispatch) {
  if (!profile?.id) {
    return false;
  }

  // Increment global operation counter
  dispatch(incrementOperation('profile'));

  // Update Redux optimistically
  const oldValue = profile[field];
  const optimisticProfile = { ...profile, [field]: value };
  dispatch(setProfile(optimisticProfile));

  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({ [field]: value })
      .eq('id', profile.id)
      .select()
      .maybeSingle();

    if (error) {
      // Set error message
      const errorMessage = error.message?.toLowerCase().includes('network') || error.message?.toLowerCase().includes('fetch')
        ? 'Error: No internet connection'
        : 'Error: Failed to save';
      dispatch(setOperationError({ type: 'profile', error: errorMessage }));
      // Revert Redux on error
      dispatch(setProfile({ ...profile, [field]: oldValue }));
      return false;
    }

    if (data) {
      // Update Redux with fresh data from Supabase
      dispatch(setProfile(data));
      return true;
    }

    return false;
  } catch (err) {
    // Set error message
    const errorMessage = err.message?.toLowerCase().includes('network') || err.message?.toLowerCase().includes('fetch')
      ? 'Error: No internet connection'
      : 'Error: Failed to save';
    dispatch(setOperationError({ type: 'profile', error: errorMessage }));
    // Revert Redux on error
    dispatch(setProfile({ ...profile, [field]: oldValue }));
    return false;
  } finally {
    // Decrement global operation counter
    dispatch(decrementOperation('profile'));
  }
}
